import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, theme, Menu, message, Dropdown, Avatar, Space } from 'antd';
import { UserOutlined, LogoutOutlined, DownOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import './App.css';
import Login from './pages/Login';
import DataSubmissionPage from './pages/DataSubmission';
import Projects from './pages/Projects';
import ProtocolProjects from './pages/ProtocolProjects';
import ProjectForm from './pages/ProjectForm';
import ProtocolProjectForm from './pages/ProtocolProjectForm';
import Devices from './pages/Devices';
import DeviceForm from './pages/DeviceForm';
import DeviceManagement from './pages/DeviceManagement';
import FormConfigManagement from './pages/FormConfigManagement';
import DynamicFormExample from './pages/DynamicFormExample';
import DynamicFormNav from './pages/DynamicFormNav';
import EzvizVideoManagement from './pages/EzvizVideoManagement';
import SettlementCenter from './pages/SettlementCenter';
import DeviceQuery from './pages/DeviceQuery';

const { Content, Header } = Layout;

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Protected route component for UID 7 only
const ProtectedRouteForUID7 = ({ children }: { children: React.ReactNode }) => {
  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has UID 7
  let isUID7 = false;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      isUID7 = user.uid === 7;
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  
  if (!isUID7) {
    // Show message and redirect to projects page if not UID 7
    message.error('您没有权限访问动态表单系统，只有UID为7的用户才能访问');
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// User menu component that uses useNavigate
const UserMenu = ({ userName, isUID7 }: { userName: string; isUID7: boolean }) => {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    // Clear user data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Show success message
    message.success('退出登录成功');
    
    // Redirect to login page
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'userInfo',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div>用户名: {userName}</div>
          {isUID7 && <div>管理员用户</div>}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      danger: true,
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
      <a onClick={(e) => e.preventDefault()} style={{ marginLeft: 16 }}>
        <Space>
          <Avatar icon={<UserOutlined />} />
          <span>{userName}</span>
          <DownOutlined style={{ fontSize: '12px' }} />
        </Space>
      </a>
    </Dropdown>
  );
};

// Navigation links component with active state
const NavLinks = ({ isUID7 }: { isUID7: boolean }) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Check if path is active
  const isProjectsActive = pathname === '/' || pathname === '/projects' || pathname.startsWith('/project-');
  const isDynamicFormActive = pathname.includes('dynamic-form') || pathname === '/device-management' || pathname === '/form-config-management';
  const isEzvizActive = pathname === '/ezviz-video-management';
  const isSettlementActive = pathname === '/settlement-center';
  const isDeviceQueryActive = pathname === '/device-query';
  
  // Container style for each nav item
  const navItemContainerStyle = {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative' as const,
    marginRight: 24
  };
  
  // Style for all nav links
  const navLinkStyle = {
    fontSize: '14px',
    padding: '0 2px',
    color: '#666'
  };
  
  // Style for active nav links
  const activeNavLinkStyle = {
    ...navLinkStyle,
    color: '#1677ff',
    fontWeight: 500 as const
  };
  
  // Style for the active indicator
  const indicatorStyle = {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: '#1677ff'
  };
  
  return (
    <div style={{ marginLeft: 40, display: 'flex', height: '100%' }}>
      <div style={navItemContainerStyle}>
        <Link to="/" style={isProjectsActive ? activeNavLinkStyle : navLinkStyle}>
          项目列表
        </Link>
        {isProjectsActive && <div style={indicatorStyle}></div>}
      </div>
      
      <div style={navItemContainerStyle}>
        <Link to="/device-query" style={isDeviceQueryActive ? activeNavLinkStyle : navLinkStyle}>
          设备查询
        </Link>
        {isDeviceQueryActive && <div style={indicatorStyle}></div>}
      </div>
      
      {isUID7 && (
        <div style={navItemContainerStyle}>
          <Link to="/form-config-management" style={isDynamicFormActive ? activeNavLinkStyle : navLinkStyle}>
            动态表单系统
          </Link>
          {isDynamicFormActive && <div style={indicatorStyle}></div>}
        </div>
      )}
      
      <div style={navItemContainerStyle}>
        <Link to="/ezviz-video-management" style={isEzvizActive ? activeNavLinkStyle : navLinkStyle}>
          萤石云管理
        </Link>
        {isEzvizActive && <div style={indicatorStyle}></div>}
      </div>
      
      <div style={navItemContainerStyle}>
        <Link to="/settlement-center" style={isSettlementActive ? activeNavLinkStyle : navLinkStyle}>
          结算中心
        </Link>
        {isSettlementActive && <div style={indicatorStyle}></div>}
      </div>
    </div>
  );
};

// App layout component
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  // Check if user has UID 7
  const [isUID7, setIsUID7] = useState(false);
  const [userName, setUserName] = useState('');
  
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsUID7(user.uid === 7);
        setUserName(user.realName || '用户');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  return (
    <Layout style={{ 
      minHeight: '100vh',
      height: 'auto', /* Ensure it only takes the space it needs */
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 24px', 
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Link to="/">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.svg" alt="Logo" className="app-logo" />
              <span className="app-title">综合数据管理系统</span>
            </div>
          </Link>
          {/* Navigation links with active state */}
          <NavLinks isUID7={isUID7} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserMenu userName={userName} isUID7={isUID7} />
        </div>
      </Header>
      <Content style={{ 
        padding: '0', 
        background: '#f0f2f5',
        height: 'auto', /* Ensure it only takes the space it needs */
        flex: '1 0 auto' /* Allow it to grow but not shrink */
      }}>
        <div className="page-container">
          {children}
        </div>
      </Content>
    </Layout>
  );
};

function App() {
  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check for token on component mount and when localStorage changes
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };

    // Initial check
    checkAuth();

    // Listen for storage events (when localStorage changes)
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 6,
        },
      }}
    >
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Projects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-query" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceQuery />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dynamic-form-nav" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DynamicFormNav />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            <Route 
              path="/projects" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Projects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project-create" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/project-edit/:id" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-projects/:protocolId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-project-create/:protocolId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjectForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-project-edit/:protocolId/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjectForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            {/* Device routes */}
            <Route 
              path="/devices/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Devices />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-create/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-edit/:projectId/:deviceId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/data-submission" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataSubmissionPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            {/* 动态表单管理路由 */}
            <Route 
              path="/device-management" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DeviceManagement />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            <Route 
              path="/form-config-management" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <FormConfigManagement />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            {/* 动态表单示例路由 */}
            <Route 
              path="/dynamic-form-example" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DynamicFormExample />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            {/* Ezviz Cloud Video Management Route */}
            <Route 
              path="/ezviz-video-management" 
              element={
                  <AppLayout>
                    <EzvizVideoManagement />
                  </AppLayout>
              } 
            />
            {/* Settlement Center Route */}
            <Route 
              path="/settlement-center" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SettlementCenter />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
      </Router>
    </ConfigProvider>
  );
}

export default App;
